import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore"
import SendIcon from "@mui/icons-material/Send"
import { AlertTitle, Stack } from "@mui/material"
import { Dispatch, ReactElement, SetStateAction, useState } from "react"
import { useForm } from "react-hook-form"

import SubmitCxSuccess from "./submitCxSuccess"
import {
    GENERIC_DISPLAY_DETAIL,
    SUPPORT_REFER_ENTITY,
} from "../../../errors/errorMessages"
import useSubmitCx from "../../../services/middleware/hooks/commissioning/installer/useSubmitCx"
import {
    CxFormClasses,
    LT_368_FORM_CLASS,
} from "../../../services/middleware/types/commissioning/installer/launchCxFormTypes"
import { GRID_SPACING } from "../../../themes/defaultThemeParams"
import AppLoadingButton from "../../buttons/appLoadingButton"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import ValidatedCheckBox from "../../inputs/validated/validatedCheckBox"
import DisplayIfElse from "../../wrappers/displayIfElse"

const DECLARATION_FOR_CX_G98 =
    "I declare that the relevant generating units/Micro generators and the installation which together form a Power Generating Module/Micro-generating plant at the above address, conform to the requirements of EREC G99/G98 respectively."
const DECLARATION_FOR_CX_G99_COMPLIANCE =
    "I confirm that the compliance with the requirements of EREC G99 and G100 (if applicable) is achieved"
const DECLARATION_FOR_CX_G99_SUCCESSFULLY =
    "I confirm that all the commissioning checks have been successfully completed"

interface IDeclarationSubmitProps {
    formClass: CxFormClasses
    setCurrentStepIndex: Dispatch<SetStateAction<number>>
    formVersion: number
    applicationId: string
    formData: Record<string, unknown>
    dno: string
}

/**
 * Declaration and submit buttons for the commissioning form
 */
const DeclarationSubmit = ({
    formClass,
    setCurrentStepIndex,
    formVersion,
    applicationId,
    formData,
    dno,
}: IDeclarationSubmitProps): ReactElement => {
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false)
    const { isSubmitting, error, postResponse } = useSubmitCx(
        formClass,
        formVersion,
        applicationId,
        setIsSuccessDialogOpen,
        formData
    )
    const { control, handleSubmit } = useForm()

    const onSubmit = (): void => {
        postResponse()
    }
    return (
        <>
            {isSuccessDialogOpen && (
                <SubmitCxSuccess
                    isOpen={isSuccessDialogOpen}
                    dno={dno}
                    applicationId={applicationId}
                />
            )}
            <Stack direction="column" spacing={GRID_SPACING}>
                {error && (
                    <AppAlert
                        severity={error.code === 400 ? "warning" : "error"}
                    >
                        <AlertTitle>
                            {" "}
                            {error.code === 400
                                ? "We were unable to validate the information you have entered. Please check your inputs and try again"
                                : error.message}
                        </AlertTitle>
                        {error.code === 400
                            ? `${error.displayDetail}. 
                               If you need help, please contact ${SUPPORT_REFER_ENTITY}`
                            : GENERIC_DISPLAY_DETAIL}
                    </AppAlert>
                )}
                <Stack maxWidth="80%">
                    <DisplayIfElse
                        condition={formClass === LT_368_FORM_CLASS}
                        displayIfTrue={
                            <ValidatedCheckBox
                                control={control}
                                label={DECLARATION_FOR_CX_G98}
                                required
                                registerName="confirmInformation1"
                                horizontalMode
                                checkboxOptions={{
                                    "": "I confirm",
                                }}
                            />
                        }
                        displayIfFalse={
                            <>
                                <ValidatedCheckBox
                                    control={control}
                                    label={DECLARATION_FOR_CX_G99_COMPLIANCE}
                                    required
                                    registerName="confirmInformation1"
                                    horizontalMode
                                    checkboxOptions={{
                                        "": "I confirm",
                                    }}
                                />
                                <ValidatedCheckBox
                                    control={control}
                                    label={DECLARATION_FOR_CX_G99_SUCCESSFULLY}
                                    required
                                    registerName="confirmInformation2"
                                    horizontalMode
                                    checkboxOptions={{
                                        "": "I confirm",
                                    }}
                                />
                            </>
                        }
                    />
                </Stack>
                <Stack
                    direction="row"
                    spacing={GRID_SPACING}
                    alignItems="flex-end"
                    justifyContent="flex-end"
                >
                    <AppLoadingButton
                        variant="outlined"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={() => setCurrentStepIndex((prev) => prev - 1)}
                        loading={isSubmitting}
                    >
                        Back
                    </AppLoadingButton>
                    <AppLoadingButton
                        color="success"
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        endIcon={<SendIcon />}
                        loading={isSubmitting}
                    >
                        Submit
                    </AppLoadingButton>
                </Stack>
            </Stack>
        </>
    )
}
export default DeclarationSubmit
